import { useEffect, useState } from "react";
import { X, Sparkles, Download, RefreshCw, Copy, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import MemoRenderer from "./MemoRenderer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * ComplianceMemoModal — view, regenerate, and download an AI-generated
 * compliance cover memo for a project.
 *
 * Tier rules (enforced on the backend):
 *   - $59 CWMP-paid project: 1 CWMP memo + 1 Final memo included
 *   - Bundle / Subscription: unlimited regenerations
 */
const ComplianceMemoModal = ({
  isOpen,
  onClose,
  projectId,
  projectName = "Project",
  memoType = "cwmp", // "cwmp" | "final"
}) => {
  const [memo, setMemo] = useState("");
  const [loading, setLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [unlimited, setUnlimited] = useState(false);
  const [generated, setGenerated] = useState(false);
  const [copied, setCopied] = useState(false);

  const authHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Try to load an existing memo when modal opens
  useEffect(() => {
    if (!isOpen || !projectId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // Step 1: GET — always returns 200 with {memo, generated} or null if not yet generated
        const r = await fetch(`${API}/projects/${projectId}/compliance-memo?memo_type=${memoType}`, {
          credentials: "include",
          headers: authHeaders(),
        });
        if (cancelled) return;
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          toast.error(err.detail || "Failed to load memo");
          return;
        }
        const data = await r.json();
        if (data.generated && data.memo) {
          setMemo(data.memo);
          setUnlimited(!!data.regeneration_unlimited);
          setGenerated(true);
          return;
        }
        // Step 2: POST — auto-generate first memo
        const gen = await fetch(
          `${API}/projects/${projectId}/compliance-memo?memo_type=${memoType}`,
          {
            method: "POST",
            credentials: "include",
            headers: { ...authHeaders(), "Content-Type": "application/json" },
          }
        );
        const genData = await gen.json();
        if (cancelled) return;
        if (!gen.ok) {
          toast.error(genData.detail || "Memo generation failed");
        } else {
          setMemo(genData.memo || "");
          setUnlimited(!!genData.regeneration_unlimited);
          setGenerated(true);
        }
      } catch (e) {
        if (!cancelled) toast.error(`Memo load failed: ${e.message}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, projectId, memoType]);

  // Reset state when modal is closed so re-opening fetches fresh
  useEffect(() => {
    if (!isOpen) {
      setMemo("");
      setGenerated(false);
      setUnlimited(false);
      setCopied(false);
    }
  }, [isOpen]);

  const generate = async (isRegen) => {
    if (isRegen) setRegenerating(true);
    try {
      const r = await fetch(
        `${API}/projects/${projectId}/compliance-memo?memo_type=${memoType}`,
        {
          method: "POST",
          credentials: "include",
          headers: { ...authHeaders(), "Content-Type": "application/json" },
        }
      );
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Generation failed");
      setMemo(data.memo || "");
      setUnlimited(!!data.regeneration_unlimited);
      setGenerated(true);
      if (isRegen) toast.success("Memo regenerated");
    } catch (e) {
      toast.error(e.message);
    } finally {
      if (isRegen) setRegenerating(false);
    }
  };

  if (!isOpen) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(memo);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Copy failed — please select and copy manually");
    }
  };

  const handleDownloadTxt = () => {
    const blob = new Blob([memo], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${projectName.replace(/[^a-z0-9]+/gi, "_")}_${memoType}_memo.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-foreground/40 backdrop-blur-sm"
      onClick={onClose}
      data-testid="memo-modal"
    >
      <div
        className="relative w-full max-w-3xl max-h-[88vh] flex flex-col surface-card overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 p-6 border-b border-border bg-muted/30">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-accent/12 text-accent flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <span className="pre-headline-pill text-[0.65rem]">
                AI Compliance Memo · {memoType === "cwmp" ? "CWMP" : "Final Verification"}
              </span>
              <h3 className="font-display font-bold text-xl text-foreground mt-1.5">
                {projectName}
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Plain-English cover letter for your building department reviewer.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors p-1"
            data-testid="memo-close-btn"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {(loading || (!memo && !generated)) && (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-10 h-10 rounded-full border-2 border-accent border-t-transparent animate-spin mb-4" />
              <p className="text-sm text-muted-foreground">
                Drafting your compliance memo… (~10–20 seconds)
              </p>
            </div>
          )}

          {!loading && memo && (
            <MemoRenderer markdown={memo} data-testid="memo-content" />
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-wrap items-center justify-between gap-3 p-5 border-t border-border bg-muted/30">
          <div className="text-xs text-muted-foreground">
            {unlimited ? (
              <span className="inline-flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-accent" />
                Unlimited regenerations (Bundle / Subscription)
              </span>
            ) : (
              <span>1 included with your plan · upgrade for unlimited</span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleCopy}
              disabled={!memo}
              className="pill-btn pill-btn-secondary text-xs disabled:opacity-40"
              data-testid="memo-copy-btn"
            >
              {copied ? <CheckCheck className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              {copied ? "Copied" : "Copy"}
            </button>
            <button
              onClick={handleDownloadTxt}
              disabled={!memo}
              className="pill-btn pill-btn-secondary text-xs disabled:opacity-40"
              data-testid="memo-download-btn"
            >
              <Download className="w-3.5 h-3.5" />
              .md
            </button>
            {unlimited && (
              <button
                onClick={() => generate(true)}
                disabled={regenerating || !memo}
                className="pill-btn pill-btn-accent text-xs disabled:opacity-40"
                data-testid="memo-regenerate-btn"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${regenerating ? "animate-spin" : ""}`} />
                {regenerating ? "Regenerating…" : "Regenerate"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComplianceMemoModal;
